<template>
  <div class="pwc-file-upload" :style="cPreview">
    <img v-if="uploading" src="./assets/loading.svg" alt="">
    <input type="file" accept="image/*" @change="handleSelect">
  </div>
</template>

<script>
import { GetData, SetData } from '@/util/buffer'

export default {
  name: 'PwcAvatar',
  props: {
    avatar: {
      type: String,
      default: ''
    },
    upload: {
      type: Function,
      default: () => {}
    }
  },
  data() {
    return {
      uploading: false,
      fixedOrientationUrl: ''
    }
  },
  computed: {
    cPreview() {
      if (!this.fixedOrientationUrl) {
        return {}
      }
      return {
        // backgroundImage: `url(${this.localUrl || this.avatar})`,
        backgroundImage: `url(${this.fixedOrientationUrl})`,
        backgroundSize: 'cover'
      }
    }
  },
  async created() {
    if (GetData((this.avatar || '').trim())) {
      this.fixedOrientationUrl = (this.avatar || '').trim()
      return
    }
    if (this.avatar) {
      const info = await this.__getRemoteImageInfo(this.avatar)
      const orientation = await this.__getOrientation(info.blob)
      if (orientation < 0) {
        this.fixedOrientationUrl = this.avatar
      } else {
        this.fixedOrientationUrl = await this.__resetOrientation(info.dataUrl, orientation)
      }
      SetData((this.avatar || '').trim(), this.fixedOrientationUrl)
    }
  },
  methods: {
    async handleSelect(e) {
      const dataUrl = await this.__readFile(e.target.files[0])
      const orientation = await this.__getOrientation(e.target.files[0])
      this.fixedOrientationUrl = await this.__resetOrientation(dataUrl, orientation)

      this.uploading = true
      const remoteUrl = await this.upload(e, '', false)
      this.uploading = false
      this.$emit('update:avatar', remoteUrl)
    },

    // hack for browser not recognize exif info
    // https://stackoverflow.com/questions/7584794/accessing-jpeg-exif-rotation-data-in-javascript-on-the-client-side/32490603#32490603
    // https://stackoverflow.com/questions/20600800/js-client-side-exif-orientation-rotate-and-mirror-jpeg-images/20600801#20600801
    __getOrientation(file) {
      return new Promise((resolve) => {
        const reader = new FileReader()
        reader.onload = function(e) {
          const view = new DataView(e.target.result)
          if (view.getUint16(0, false) !== 0xFFD8) {
            return resolve(-2)
          }
          const length = view.byteLength; let offset = 2
          while (offset < length) {
            if (view.getUint16(offset + 2, false) <= 8) return resolve(-1)
            const marker = view.getUint16(offset, false)
            offset += 2
            if (marker === 0xFFE1) {
              if (view.getUint32(offset += 2, false) !== 0x45786966) {
                return resolve(-1)
              }

              const little = view.getUint16(offset += 6, false) === 0x4949
              offset += view.getUint32(offset + 4, little)
              const tags = view.getUint16(offset, little)
              offset += 2
              for (let i = 0; i < tags; i++) {
                if (view.getUint16(offset + (i * 12), little) === 0x0112) {
                  return resolve(view.getUint16(offset + (i * 12) + 8, little))
                }
              }
            } else if ((marker & 0xFF00) !== 0xFF00) {
              break
            } else {
              offset += view.getUint16(offset, false)
            }
          }
          return resolve(-1)
        }
        reader.readAsArrayBuffer(file)
      })
    },
    __resetOrientation(srcBase64, srcOrientation) {
      return new Promise((resolve) => {
        const img = new Image()

        img.onload = function() {
          const width = img.width
          const height = img.height
          const canvas = document.createElement('canvas')
          const ctx = canvas.getContext('2d')

          // set proper canvas dimensions before transform & export
          if (srcOrientation > 4 && srcOrientation < 9) {
            canvas.width = height
            canvas.height = width
          } else {
            canvas.width = width
            canvas.height = height
          }

          // transform context before drawing image
          switch (srcOrientation) {
            case 2: ctx.transform(-1, 0, 0, 1, width, 0); break
            case 3: ctx.transform(-1, 0, 0, -1, width, height); break
            case 4: ctx.transform(1, 0, 0, -1, 0, height); break
            case 5: ctx.transform(0, 1, 1, 0, 0, 0); break
            case 6: ctx.transform(0, 1, -1, 0, height, 0); break
            case 7: ctx.transform(0, -1, -1, 0, height, width); break
            case 8: ctx.transform(0, -1, 1, 0, 0, width); break
            default: break
          }

          // draw image
          ctx.drawImage(img, 0, 0)

          // export base64
          resolve(canvas.toDataURL())
        }

        img.src = srcBase64
      })
    },
    __getRemoteImageInfo(url) {
      if (!url) {
        url = ''
      }
      return new Promise((resolve, reject) => {
        try {
          if (GetData(url.trim())) {
            return resolve(GetData(url.trim()))
          }
          const xhr = new XMLHttpRequest()
          const reader = new FileReader()
          const info = {}
          xhr.open('GET', url)
          xhr.responseType = 'blob'
          xhr.onerror = function() { reject('Network error.') }
          reader.onload = (e) => {
            info.dataUrl = e.target.result
            SetData(url.trim(), info)
            resolve(info)
          }
          xhr.onload = function() {
            if (xhr.status === 200) {
              info.blob = xhr.response
              reader.readAsDataURL(xhr.response)
            } else {
              reject('Loading error:' + xhr.statusText)
            }
          }
          xhr.send()
        } catch (err) { reject(err.message) }
      })
    },
    __readFile(file) {
      const reader = new FileReader()
      return new Promise((resolve) => {
        reader.readAsDataURL(file)
        reader.onload = (e) => {
          resolve(e.target.result)
        }
      })
    }
  }
}
</script>

<style lang="scss">
$pwc-avatar-size:80px;

.pwc-file-upload{
  box-sizing: border-box;
  margin: 4px;
  background-image: url('./assets/camera.png');
  background-size: 50% 50%;
  background-position: center;
  background-repeat: no-repeat;
  position:relative;
  height:$pwc-avatar-size;
  width:$pwc-avatar-size;
  border-radius: 50%;
  overflow:hidden;
  display:flex;
  justify-content:center;
  align-items: center;
  border:4px solid #fff;
  box-shadow: inset 0 1.5px 3px 0 rgba(0,0,0,.15), 0 1.5px 3px 0 rgba(0,0,0,.15);

  input[type='file']{
    position:absolute;
    height:$pwc-avatar-size;
    width:$pwc-avatar-size;
    top:0;
    left:0;
    opacity:0;
    cursor:pointer;
  }
  img {
    height: 80px;
    width: 80px;
  }
}
</style>

